// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Timeline
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS analytics timeline for recent event
 * activity. The component maps shared timeline events into a compact log view
 * with event icons and localized timestamps.
 */

import { EVENT_ICONS, type TimelineEvent } from "@geenius/analytics";
import { Card, CardContent } from "@geenius/ui/solidjs-css";
import { type Component, For } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS CSS analytics timeline.
 *
 * @property events Timeline events to render in descending recency order.
 * @property maxVisible Maximum number of timeline rows to display.
 * @property class Optional class name merged into the timeline wrapper.
 */
export interface TimelineProps {
  events: TimelineEvent[];
  maxVisible?: number;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders a SolidJS CSS timeline for recent analytics events.
 *
 * @param props Timeline events and the optional visible-row limit.
 * @returns A timeline log element for analytics activity.
 */
export const Timeline: Component<TimelineProps> = (props) => {
  const copy = createAnalyticsCopy();

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardContent>
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardContent>
        <div
          aria-label={copy.t("analytics.dashboard.recentEvents")}
          aria-live="polite"
          aria-relevant="additions text"
          class="gn-analytics-timeline"
          role="log"
        >
          <For each={props.events.slice(0, props.maxVisible ?? 20)}>
            {(event) => (
              <div class="gn-analytics-timeline__event">
                <span class="gn-analytics-timeline__icon" aria-hidden="true">
                  {EVENT_ICONS[event.event] ?? "⚡"}
                </span>
                <span class="gn-analytics-timeline__name">{event.event}</span>
                {event.userId && (
                  <span class="gn-analytics-timeline__user">
                    {event.userId}
                  </span>
                )}
                <span class="gn-analytics-timeline__time">
                  {copy.formatTime(event.timestamp)}
                </span>
              </div>
            )}
          </For>
        </div>
      </CardContent>
    </Card>
  );
};
